(ns common.opensearch.totals
  "The totals of the number columns of a listing: the sum or the average of the
   rows that the search finds. Amounts in different currencies cannot be added up,
   so a currency column has one total for each currency. A row with no currency
   counts in the default currency of the tenant, as the grid shows it.

   A total is `{:id .. :field .. :kind .. :currency-field ..}`: the flat field of the
   number, \"sum\" or \"avg\", and the flat field of its currency, or nil."
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.opensearch.filter-queries :as filter-queries]))

(def no-currency
  "The key of the rows with no currency in a total by currency."
  "__no-currency__")

(defn single-valued-path?
  "True when each link of `field-path` before its last field gives one record.
   `field-schema-of` gives the field schema of an attribute. A reverse link, such
   as `:investor/_legal-entity`, can give many records.

   A currency total needs it. The document of a row holds the amounts and the
   currencies of many linked records in two separate lists, so a total by
   currency would add every amount to every currency of the row."
  [field-path field-schema-of]
  (->> (butlast field-path)
       (every? (fn [attribute]
                 (and (not (string/starts-with? (name attribute) "_"))
                      (not= :data.type/ref-multi
                            (:field/data-type (field-schema-of attribute))))))))

(def max-currencies
  "The number of currency buckets of a total: each ISO 4217 currency, and the
   bucket of the rows with no currency. A total with a limit below this would
   leave out the currencies after the limit."
  (inc (count currencies/iso-4217-currencies)))

(defn- aggregation
  "The aggregation of a total. An average by currency has the sum and the count of
   each currency, so that the rows with no currency can join the default currency
   with their weight (`with-default-currency`)."
  [{:keys [field kind currency-field]}]
  (let [value {(keyword kind) {:field field}}]
    (if currency-field
      {:terms {:field   (filter-queries/terms-field currency-field :data.type/currency-symbol)
               :size    max-currencies
               :missing no-currency}
       :aggs  (if (= "avg" kind)
                {:sum   {:sum {:field field}}
                 :count {:value_count {:field field}}}
                {:value value})}
      value)))

(defn aggregations
  "The `aggs` of an OpenSearch request for `totals`, keyed by the id of each total."
  [totals]
  (into {}
        (map (fn [{:keys [id] :as total}]
               [(keyword id) (aggregation total)]))
        totals))

(defn- currency-key
  "The currency of a bucket as a keyword, for example `:eur` for \":eur\" or \"eur\".
   The rows with no currency keep the key `no-currency`, a text that a response can
   carry as a key."
  [bucket-key]
  (if (= no-currency bucket-key)
    no-currency
    (keyword (subs (str bucket-key) (if (= \: (first (str bucket-key))) 1 0)))))

(defn- bucket-value
  "The value of a bucket of a currency: a number, or `{:sum .. :count ..}` for an
   average."
  [kind bucket]
  (if (= "avg" kind)
    (when (pos? (or (get-in bucket [:count :value]) 0))
      {:sum   (get-in bucket [:sum :value])
       :count (get-in bucket [:count :value])})
    (get-in bucket [:value :value])))

(defn- total-value
  [{:keys [kind currency-field]} aggregation-result]
  (if currency-field
    {:by-currency (->> (:buckets aggregation-result)
                       (keep (fn [bucket]
                               (when-some [value (bucket-value kind bucket)]
                                 [(currency-key (:key bucket)) value])))
                       (into {}))}
    {:value (:value aggregation-result)}))

(defn parse
  "The values of `totals` in the `aggregations` of an OpenSearch response (or of
   a bucket): `{id {:value n}}`, or `{id {:by-currency {currency n}}}` for a
   currency field, where the currency `no-currency` is the rows with no currency."
  [totals aggregations]
  (into {}
        (keep (fn [{:keys [id] :as total}]
                (when-let [result (or (get aggregations (keyword id))
                                      (get aggregations id))]
                  [id (total-value total result)])))
        totals))

(defn- add-values
  [a b]
  (if (map? b)
    (merge-with + a b)
    (+ (or a 0) b)))

(defn- resolve-value
  "A number, or the average of `{:sum .. :count ..}`."
  [value]
  (if (map? value)
    (/ (:sum value) (:count value))
    value))

(defn with-default-currency
  "The value of each currency of `by-currency`, with the rows with no currency in
   `default-currency`. An average joins the sums and the counts first, so the rows
   with no currency count with their weight. The key of a currency can be a
   keyword or a text, as a response carries it."
  [by-currency default-currency]
  (let [by-currency (into {}
                          (map (fn [[currency value]]
                                 [(if (= no-currency (some-> currency name))
                                    no-currency
                                    (keyword currency))
                                  value]))
                          by-currency)
        default-currency (some-> default-currency keyword)
        merged (if-let [none (get by-currency no-currency)]
                 (-> by-currency
                     (dissoc no-currency)
                     (update default-currency add-values none))
                 by-currency)]
    (into {}
          (map (fn [[currency value]]
                 [currency (resolve-value value)]))
          merged)))

(defn- add
  [a b]
  (cond
    (nil? a) b
    (nil? b) a
    (or (:by-currency a) (:by-currency b))
    {:by-currency (merge-with + (:by-currency a) (:by-currency b))}
    :else
    {:value (+ (or (:value a) 0) (or (:value b) 0))}))

(defn sum-of
  "The total of `totals`, totals of the same field: a sum of the values, by
   currency for a currency field. Nil when the totals are averages (`kind` is not
   \"sum\"), which averages do not give."
  [kind totals]
  (when (= "sum" kind)
    (reduce add nil totals)))

(defn merge-parsed
  "The totals of several buckets that show as one, from their parsed totals
   `parsed-list` (see `parse`). A sum is the sum of the sums. An average of
   several buckets is left out, as the averages do not give it. One bucket keeps
   its totals."
  [totals parsed-list]
  (if (<= (count parsed-list) 1)
    (first parsed-list)
    (into {}
          (keep (fn [{:keys [id kind]}]
                  (when-some [total (sum-of kind (keep (fn [parsed] (get parsed id)) parsed-list))]
                    [id total])))
          totals)))

(defn total-fields
  "The fields that `totals` read, so that a caller can check that the user can read
   them."
  [totals]
  (->> totals
       (mapcat (juxt :field :currency-field))
       (remove nil?)
       (distinct)
       (vec)))
